/**
 * 隐私同意对话框
 * 符合 GDPR, CCPA, PDPO, PIPL 要求
 */

import React, { useState, useEffect } from 'react';
import { PrivacyApiService } from '../services/api';
import { useTranslation } from '../hooks/useTranslation';
import { detectChinaRegion } from '../contexts/AuthContext';
import PrivacyPolicyPage from './PrivacyPolicyPage';

interface PrivacyConsentProps {
    currentLanguage: string;
    onAccept: () => void;
}

const PrivacyConsent: React.FC<PrivacyConsentProps> = ({ currentLanguage, onAccept }) => {
    const { t } = useTranslation(currentLanguage);
    const [consents, setConsents] = useState({
        essential: true, // 必要 Cookie - 始终启用
        analytics: false,
        marketing: false,
    });
    const [showDetails, setShowDetails] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

    // 检测区域用于显示适用的法规
    const isChinaRegion = detectChinaRegion();
    const region = isChinaRegion ? 'CN' : 'EU';

    // 获取适用的法规名称
    const getApplicableLaw = () => {
        if (isChinaRegion) {
            return t('piplCompliance') || '个人信息保护法 (PIPL)';
        }
        return t('gdprCompliance') || 'GDPR';
    };

    // 接受所有
    const handleAcceptAll = async () => {
        setIsSubmitting(true);
        try {
            await PrivacyApiService.recordAnonymousConsent([
                { type: 'essential', granted: true },
                { type: 'analytics', granted: true },
                { type: 'marketing', granted: true },
            ]);
            localStorage.setItem('consent_accepted', 'all');
            localStorage.setItem('consent_date', new Date().toISOString());
            onAccept();
        } catch (error) {
            console.error('Failed to record consent:', error);
            // 即使 API 失败也允许继续使用
            localStorage.setItem('consent_accepted', 'all');
            onAccept();
        } finally {
            setIsSubmitting(false);
        }
    };

    // 仅接受必要
    const handleAcceptEssential = async () => {
        setIsSubmitting(true);
        try {
            await PrivacyApiService.recordAnonymousConsent([
                { type: 'essential', granted: true },
                { type: 'analytics', granted: false },
                { type: 'marketing', granted: false },
            ]);
            localStorage.setItem('consent_accepted', 'essential');
            localStorage.setItem('consent_date', new Date().toISOString());
            onAccept();
        } catch (error) {
            console.error('Failed to record consent:', error);
            localStorage.setItem('consent_accepted', 'essential');
            onAccept();
        } finally {
            setIsSubmitting(false);
        }
    };

    // 保存自定义设置
    const handleSavePreferences = async () => {
        setIsSubmitting(true);
        try {
            await PrivacyApiService.recordAnonymousConsent([
                { type: 'essential', granted: true },
                { type: 'analytics', granted: consents.analytics },
                { type: 'marketing', granted: consents.marketing },
            ]);
            localStorage.setItem('consent_accepted', 'custom');
            localStorage.setItem('consent_preferences', JSON.stringify(consents));
            localStorage.setItem('consent_date', new Date().toISOString());
            onAccept();
        } catch (error) {
            console.error('Failed to record consent:', error);
            localStorage.setItem('consent_accepted', 'custom');
            onAccept();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl shadow-2xl">
                {/* Header */}
                <div className="px-6 pt-6 pb-4">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">{t('privacyTitle') || 'Your Privacy Matters'}</h2>
                                <p className="text-xs text-gray-500">{getApplicableLaw()}</p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 leading-relaxed">
                            {t('privacyIntro') || 'We use cookies and similar technologies to provide you with the best experience. You can customize your preferences below.'}
                        </p>
                    </div>

                    {/* 详细设置 (可展开) */}
                    {showDetails && (
                        <div className="px-6 pb-4 space-y-3">
                            <div className="bg-gray-50 rounded-lg p-4">
                                {/* Essential Cookies */}
                                <div className="flex items-center justify-between py-2">
                                    <div>
                                        <p className="font-medium text-sm text-gray-900">{t('essentialCookies') || 'Essential Cookies'}</p>
                                        <p className="text-xs text-gray-500">{t('essentialDesc') || 'Required for the website to function'}</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={true}
                                        disabled
                                        className="w-5 h-5 text-gray-600 rounded"
                                    />
                                </div>

                                {/* Analytics Cookies */}
                                <div className="flex items-center justify-between py-2 border-t border-gray-200">
                                    <div>
                                        <p className="font-medium text-sm text-gray-900">{t('analyticsCookies') || 'Analytics Cookies'}</p>
                                        <p className="text-xs text-gray-500">{t('analyticsDesc') || 'Help us improve our service'}</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={consents.analytics}
                                        onChange={(e) => setConsents({ ...consents, analytics: e.target.checked })}
                                        className="w-5 h-5 text-gray-600 rounded cursor-pointer"
                                    />
                                </div>

                                {/* Marketing Cookies */}
                                <div className="flex items-center justify-between py-2 border-t border-gray-200">
                                    <div>
                                        <p className="font-medium text-sm text-gray-900">{t('marketingCookies') || 'Marketing Cookies'}</p>
                                        <p className="text-xs text-gray-500">{t('marketingDesc') || 'Used for personalized content'}</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={consents.marketing}
                                        onChange={(e) => setConsents({ ...consents, marketing: e.target.checked })}
                                        className="w-5 h-5 text-gray-600 rounded cursor-pointer"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 按钮区域 */}
                    <div className="px-6 pb-6">
                        {showDetails ? (
                            <div className="space-y-2">
                                <button
                                    onClick={handleSavePreferences}
                                    disabled={isSubmitting}
                                    className="w-full py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
                                >
                                    {t('savePreferences') || 'Save Preferences'}
                                </button>
                                <button
                                    onClick={() => setShowDetails(false)}
                                    className="w-full py-2 text-gray-600 text-sm hover:text-gray-800"
                                >
                                    {t('back') || 'Back'}
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <button
                                    onClick={handleAcceptAll}
                                    disabled={isSubmitting}
                                    className="w-full py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
                                >
                                    {t('acceptAll') || 'Accept All'}
                                </button>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleAcceptEssential}
                                        disabled={isSubmitting}
                                        className="flex-1 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    >
                                        {t('essentialOnly') || 'Essential Only'}
                                    </button>
                                    <button
                                        onClick={() => setShowDetails(true)}
                                        className="flex-1 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                                    >
                                        {t('customize') || 'Customize'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 隐私政策链接 */}
                    <div className="bg-gray-50 px-6 py-3 text-center sm:rounded-b-2xl">
                        <button
                            type="button"
                            onClick={() => setShowPrivacyPolicy(true)}
                            className="text-xs text-gray-600 hover:underline hover:text-gray-900"
                        >
                            {t('readPrivacyPolicy') || 'Read our Privacy Policy'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Privacy Policy Modal */}
            {showPrivacyPolicy && (
                <PrivacyPolicyPage
                    currentLanguage={currentLanguage}
                    onClose={() => setShowPrivacyPolicy(false)}
                />
            )}
        </div>
    );
};

export default PrivacyConsent;
